/**
 * Role Utility Functions
 * 
 * This module provides utilities for working with user roles in the application.
 * It centralizes role logic and capabilities.
 */

/**
 * Site user roles enum
 */
export const SITE_USER_ROLES = {
  MEMBER: 'member',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin'
};

/**
 * Club user roles enum
 */
export const CLUB_USER_ROLES = {
  GUEST: 'guest',
  MEMBER: 'member',
  ADMIN: 'admin',
  OWNER: 'owner',
  HOST: 'host'
};

/**
 * Role capabilities configuration
 * This maps roles to their capabilities
 */
export const SITE_ROLE_CAPABILITIES = {
  // Default capabilities
  DEFAULT: {
    canCreateClub: true,
    canJoinClub: true,
    canViewPublicSessions: true,
    canSignUpForPublicSessions: true
  },
  
  // Member capabilities
  [SITE_USER_ROLES.MEMBER]: {
    canCreateClub: true,
    canJoinClub: true,
    canViewPublicSessions: true,
    canSignUpForPublicSessions: true
  },
  
  // Admin capabilities
  [SITE_USER_ROLES.ADMIN]: {
    canCreateClub: true,
    canJoinClub: true,
    canViewPublicSessions: true,
    canSignUpForPublicSessions: true,
    canManageUsers: true,
    canViewAllClubs: true
  },
  
  // Super admin capabilities
  [SITE_USER_ROLES.SUPER_ADMIN]: {
    canCreateClub: true,
    canJoinClub: true,
    canViewPublicSessions: true,
    canSignUpForPublicSessions: true,
    canManageUsers: true,
    canViewAllClubs: true,
    canManageAllClubs: true,
    canManageAllSessions: true,
    canAccessAdminPanel: true
  }
};

/**
 * Club role capabilities configuration
 * This maps club roles to their capabilities
 */
export const CLUB_ROLE_CAPABILITIES = {
  // Default capabilities
  DEFAULT: {
    canViewClubSessions: true,
    canSignUpForSessions: true,
    canViewClubMembers: true
  },
  
  // Guest capabilities
  [CLUB_USER_ROLES.GUEST]: {
    canViewClubSessions: true,
    canSignUpForSessions: false,
    canViewClubMembers: false
  },
  
  // Member capabilities
  [CLUB_USER_ROLES.MEMBER]: {
    canViewClubSessions: true,
    canSignUpForSessions: true,
    canViewClubMembers: true
  },
  
  // Host capabilities
  [CLUB_USER_ROLES.HOST]: {
    canViewClubSessions: true,
    canSignUpForSessions: true,
    canViewClubMembers: true,
    canCreateSessions: true,
    canManageSessions: true
  },
  
  // Admin capabilities
  [CLUB_USER_ROLES.ADMIN]: {
    canViewClubSessions: true,
    canSignUpForSessions: true,
    canViewClubMembers: true,
    canCreateSessions: true,
    canManageSessions: true,
    canManageClubMembers: true,
    canManageClubSettings: true,
    canInviteMembers: true
  },
  
  // Owner capabilities
  [CLUB_USER_ROLES.OWNER]: {
    canViewClubSessions: true,
    canSignUpForSessions: true,
    canViewClubMembers: true,
    canCreateSessions: true,
    canManageSessions: true,
    canManageClubMembers: true,
    canManageClubSettings: true,
    canInviteMembers: true,
    canTransferOwnership: true,
    canDeleteClub: true
  }
};

/**
 * Get site role capabilities
 * @param {string} role - The site role
 * @returns {Object} The capabilities for the role
 */
export const getSiteRoleCapabilities = (role) => {
  return SITE_ROLE_CAPABILITIES[role] || SITE_ROLE_CAPABILITIES.DEFAULT;
};

/**
 * Get club role capabilities
 * @param {string} role - The club role
 * @returns {Object} The capabilities for the role
 */
export const getClubRoleCapabilities = (role) => {
  return CLUB_ROLE_CAPABILITIES[role] || CLUB_ROLE_CAPABILITIES.DEFAULT;
};

/**
 * Check if a user has a specific site capability
 * @param {string} role - The user's site role
 * @param {string} capability - The capability to check
 * @returns {boolean} True if the user has the capability
 */
export const hasSiteCapability = (role, capability) => {
  const capabilities = getSiteRoleCapabilities(role);
  return !!capabilities[capability];
};

/**
 * Check if a user has a specific club capability
 * @param {string} role - The user's club role
 * @param {string} capability - The capability to check
 * @returns {boolean} True if the user has the capability
 */
export const hasClubCapability = (role, capability) => {
  const capabilities = getClubRoleCapabilities(role);
  return !!capabilities[capability];
};

/**
 * Check if a user can manage a club
 * @param {string} siteRole - The user's site role
 * @param {string} clubRole - The user's role in the club
 * @returns {boolean} True if the user can manage the club
 */
export const canManageClub = (siteRole, clubRole) => {
  // Super admins can manage any club
  if (siteRole === SITE_USER_ROLES.SUPER_ADMIN) {
    return true;
  }
  
  // Club owners and admins can manage their club
  return clubRole === CLUB_USER_ROLES.OWNER || clubRole === CLUB_USER_ROLES.ADMIN;
};

/**
 * Check if a user can manage sessions
 * @param {string} siteRole - The user's site role
 * @param {string} clubRole - The user's role in the club
 * @returns {boolean} True if the user can manage sessions
 */
export const canManageSessions = (siteRole, clubRole) => {
  // Super admins can manage any session
  if (siteRole === SITE_USER_ROLES.SUPER_ADMIN) {
    return true;
  }
  
  // Club owners, admins, and hosts can manage sessions
  return clubRole === CLUB_USER_ROLES.OWNER || 
         clubRole === CLUB_USER_ROLES.ADMIN || 
         clubRole === CLUB_USER_ROLES.HOST;
};

/**
 * Get the default role for a new user
 * @returns {string} The default site role
 */
export const getDefaultSiteRole = () => {
  return SITE_USER_ROLES.MEMBER;
};

/**
 * Get the default role for a new club member
 * @returns {string} The default club role
 */
export const getDefaultClubRole = () => {
  return CLUB_USER_ROLES.MEMBER;
};
